<template>
  <AppHeaderBack>Update Profile</AppHeaderBack>
  <div class="min-h-screen bg-gray-100 pt-8 pb-8 px-4">
    <div class="max-w-3xl mx-auto">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="p-4 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-xl text-primary">person</span>
            <h2 class="text-lg font-semibold text-gray-800">Update Personal Information</h2>
          </div>
        </div>
        
        <div class="p-6">
          <form @submit.prevent="updateProfile" class="space-y-6">
            <!-- Name Fields -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">person</span>
                  </div>
                  <input
                    id="name"
                    v-model="name"
                    type="text"
                    placeholder="Your first name"
                    required
                    :disabled="loading"
                    class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 transition duration-200 disabled:opacity-50"
                  />
                </div>
              </div>
              <div>
                <label for="surname" class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">badge</span>
                  </div>
                  <input
                    id="surname"
                    v-model="surname"
                    type="text"
                    placeholder="Your last name"
                    :disabled="loading"
                    class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 transition duration-200 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            <!-- Email Field -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="material-symbols-outlined text-gray-400">mail</span>
                </div>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  :disabled="true"
                  class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700"
                />
              </div>
              <p class="mt-1 text-xs text-gray-500">Contact support to change your email address</p>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="loading"
              class="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200"
            >
              <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ loading ? "Updating..." : "Save Changes" }}
            </button>

            <!-- Cancel Button -->
            <router-link 
              to="/profile"
              class="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200"
            >
              Cancel
            </router-link>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { updateUserProfile, getUserProfile } from '@/composables/useFirestore';
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';

export default {
  components: { AppHeaderBack },
  data() {
    return {
      name: '',
      surname: '',
      email: ''
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'loading']),
  },
  watch: {
    profile: {
      handler(newProfile) {
        if (newProfile) {
          this.name = newProfile.name || '';
          this.surname = newProfile.surname || '';
          this.email = newProfile.email || '';
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations(['setLoading', 'setProfile']),
    async updateProfile() {
      if (!this.name) {
        this.$store.dispatch('showAlert', { 
          message: "Please enter your first name", 
          type: "error" 
        });
        return;
      }

      this.setLoading(true);
      try {
        const updateData = {
          name: this.name,
          surname: this.surname // can be empty
        };

        await updateUserProfile(this.user.uid, updateData);
        const updatedProfile = await getUserProfile(this.user.uid);
        this.setProfile(updatedProfile);

        this.$store.dispatch('showAlert', { 
          message: "Personal information updated successfully!", 
          type: "success" 
        });
        this.$router.push('/profile');
      } catch (error) {
        this.$store.dispatch('showAlert', { 
          message: error.message || "Failed to update profile", 
          type: "error" 
        });
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
/* Custom styling for disabled email input */
input:disabled {
  color: #4B5563; /* gray-700 */
  -webkit-text-fill-color: #4B5563; /* For Safari */
  opacity: 1; /* Override default opacity */
}
</style>